/**
 * SunBiz Solar Panel Layout Calculator
 * Pure JavaScript — no dependencies.
 * Converts lat/lng polygons to a grid of solar panel positions.
 */

const DEG = Math.PI / 180;

// ── Shadow calculation ─────────────────────────────────────────────────────
// Computes shadow exclusion zones for full day (morning + noon + afternoon)
// Uses winter solstice as worst-case scenario
export function computeShadowPolygons(obstaclePolygons, obstacleHeights, latitudeDeg, azimuthDeg = 180) {
  if (!obstaclePolygons?.length) return [];
  const shadowPolygons = [];
  const latRad = latitudeDeg * DEG;
  // Winter solstice declination
  const decl = -23.45 * DEG;

  // Compute sun position at multiple hours: 9am, 10:30am, noon, 1:30pm, 3pm
  const hourAngles = [-45, -22.5, 0, 22.5, 45]; // degrees from solar noon

  // For each obstacle, compute shadow at each time and combine into one exclusion polygon
  obstaclePolygons.forEach((obs, i) => {
    const height = (obstacleHeights && obstacleHeights[i] != null) ? obstacleHeights[i] : 1.0;
    if (height <= 0) return; // keep-out zone: blocks panels but casts no shadow
    const allShadowPts = [];

    hourAngles.forEach(ha => {
      const haRad = ha * DEG;
      // Sun altitude: sin(alt) = sin(lat)*sin(dec) + cos(lat)*cos(dec)*cos(ha)
      const sinAlt = Math.sin(latRad) * Math.sin(decl) + Math.cos(latRad) * Math.cos(decl) * Math.cos(haRad);
      const sunAlt = Math.asin(Math.max(0.05, sinAlt)); // clamp to avoid zero/negative

      // Sun azimuth: tan(az) = sin(ha) / (cos(ha)*sin(lat) - tan(dec)*cos(lat))
      const cosAz = (sinAlt * Math.sin(latRad) - Math.sin(decl)) / (Math.cos(sunAlt) * Math.cos(latRad));
      const sinAz = Math.sin(haRad) * Math.cos(decl) / Math.cos(sunAlt);
      let sunAzimuth = Math.atan2(sinAz, cosAz); // radians from south

      // Shadow direction = same as sun azimuth angle (measured from south)
      // because shadow falls OPPOSITE to sun, and atan2 from south already gives that
      // In geographic coords: lat_offset = cos(sunAz)*len, lng_offset = sin(sunAz)*len
      const shadowDirLat = Math.cos(sunAzimuth); // +cos for north (when sun is south, az=0)
      const shadowDirLng = Math.sin(sunAzimuth); // +sin for east

      // Shadow length
      const shadowLength = height / Math.tan(sunAlt);
      if (shadowLength > 50) return; // skip unreasonably long shadows (sun too low)

      // Offset each obstacle vertex
      obs.forEach(pt => {
        allShadowPts.push({
          lat: pt.lat + (shadowDirLat * shadowLength) / 111320,
          lng: pt.lng + (shadowDirLng * shadowLength) / (111320 * Math.cos(pt.lat * DEG)),
        });
      });
    });

    if (allShadowPts.length === 0) return;

    // Create exclusion polygon: obstacle + convex hull of all shadow tips
    // Simplified approach: combine obstacle vertices with all shadow points
    const combined = [...obs, ...allShadowPts];

    // Compute convex hull of all combined points
    const hull = convexHull(combined);
    if (hull.length >= 3) {
      shadowPolygons.push(hull);
    }
  });
  return shadowPolygons;
}

// Horizontal shadow offset (metres per metre of height) for 5 winter daytime sun positions
export function sunOffsetsPerMeter(latitudeDeg) {
  const latRad = latitudeDeg * DEG;
  const decl = -23.45 * DEG;
  const hourAngles = [-45, -22.5, 0, 22.5, 45];
  const offsets = [];
  hourAngles.forEach(ha => {
    const haRad = ha * DEG;
    const sinAlt = Math.sin(latRad) * Math.sin(decl) + Math.cos(latRad) * Math.cos(decl) * Math.cos(haRad);
    const sunAlt = Math.asin(Math.max(0.05, sinAlt));
    const cosAz = (sinAlt * Math.sin(latRad) - Math.sin(decl)) / (Math.cos(sunAlt) * Math.cos(latRad));
    const sinAz = Math.sin(haRad) * Math.cos(decl) / Math.cos(sunAlt);
    const sunAzimuth = Math.atan2(sinAz, cosAz);
    const invTan = 1 / Math.tan(sunAlt);
    if (invTan > 50) return; // sun too low
    offsets.push({ dirLat: Math.cos(sunAzimuth) * invTan, dirLng: Math.sin(sunAzimuth) * invTan });
  });
  return offsets;
}

// Parapet wall around a roof — casts a shadow band inward from each edge
export function computeParapetShadows(roofPolygon, parapetHeight, latitudeDeg) {
  if (!roofPolygon || !(parapetHeight > 0)) return [];
  const offsets = sunOffsetsPerMeter(latitudeDeg);
  const quads = [];
  for (let i = 0; i < roofPolygon.length; i++) {
    const a = roofPolygon[i], b = roofPolygon[(i + 1) % roofPolygon.length];
    offsets.forEach(o => {
      const dLat = (o.dirLat * parapetHeight) / 111320;
      const dLngA = (o.dirLng * parapetHeight) / (111320 * Math.cos(a.lat * DEG));
      const dLngB = (o.dirLng * parapetHeight) / (111320 * Math.cos(b.lat * DEG));
      quads.push([
        { lat: a.lat, lng: a.lng },
        { lat: b.lat, lng: b.lng },
        { lat: b.lat + dLat, lng: b.lng + dLngB },
        { lat: a.lat + dLat, lng: a.lng + dLngA },
      ]);
    });
  }
  return quads;
}
function convexHull(points) {
  if (points.length < 3) return points;
  // Find lowest point (min lat, then min lng)
  let pivot = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].lat < points[pivot].lat || (points[i].lat === points[pivot].lat && points[i].lng < points[pivot].lng)) {
      pivot = i;
    }
  }
  const p0 = points[pivot];

  // Sort by polar angle from pivot
  const sorted = points.filter((_, i) => i !== pivot).sort((a, b) => {
    const angleA = Math.atan2(a.lat - p0.lat, a.lng - p0.lng);
    const angleB = Math.atan2(b.lat - p0.lat, b.lng - p0.lng);
    if (angleA !== angleB) return angleA - angleB;
    const distA = (a.lat - p0.lat) ** 2 + (a.lng - p0.lng) ** 2;
    const distB = (b.lat - p0.lat) ** 2 + (b.lng - p0.lng) ** 2;
    return distA - distB;
  });

  const stack = [p0];
  for (const pt of sorted) {
    while (stack.length > 1) {
      const top = stack[stack.length - 1];
      const next = stack[stack.length - 2];
      const cross = (top.lng - next.lng) * (pt.lat - next.lat) - (top.lat - next.lat) * (pt.lng - next.lng);
      if (cross <= 0) stack.pop();
      else break;
    }
    stack.push(pt);
  }
  return stack;
}


// ── Coordinate conversion ──────────────────────────────────────────────────
export function latlngToXY(lat, lng, originLat, originLng) {
  const mPerLat = 111320;
  const mPerLng = 111320 * Math.cos(originLat * DEG);
  return { x: (lng - originLng) * mPerLng, y: (lat - originLat) * mPerLat };
}

export function xyToLatlng(x, y, originLat, originLng) {
  const mPerLat = 111320;
  const mPerLng = 111320 * Math.cos(originLat * DEG);
  return { lat: originLat + y / mPerLat, lng: originLng + x / mPerLng };
}

// ── Geometry helpers ────────────────────────────────────────────────────────
export function polygonCentroid(latlngs) {
  const n = latlngs.length;
  return {
    lat: latlngs.reduce((s, p) => s + p.lat, 0) / n,
    lng: latlngs.reduce((s, p) => s + p.lng, 0) / n,
  };
}

// Ray-casting point-in-polygon (XY space)
export function pointInPolygonXY(pt, poly) {
  const { x, y } = pt;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
      inside = !inside;
  }
  return inside;
}

// Shrink polygon inward by `setback` metres — proper uniform edge offset
// Moves each edge inward by `setback` perpendicular distance, then finds new vertex intersections
export function shrinkPolygon(poly, setback) {
  if (setback <= 0 || poly.length < 3) return poly;

  const n = poly.length;

  // Determine winding direction via signed area
  let signedArea = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    signedArea += (poly[i].x * poly[j].y - poly[j].x * poly[i].y);
  }
  // signedArea > 0 → CCW, signedArea < 0 → CW
  const sign = signedArea >= 0 ? 1 : -1;

  // Compute inward-offset lines for each edge
  const offsetEdges = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = poly[j].x - poly[i].x;
    const dy = poly[j].y - poly[i].y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    // Inward normal: for CCW polygon, inward = left of edge direction = (-dy, dx)
    const nx = sign * (-dy / len);
    const ny = sign * (dx / len);
    offsetEdges.push({
      x1: poly[i].x + nx * setback,
      y1: poly[i].y + ny * setback,
      x2: poly[j].x + nx * setback,
      y2: poly[j].y + ny * setback,
    });
  }

  // Find intersection of consecutive offset edges to get new vertices
  const result = [];
  for (let i = 0; i < n; i++) {
    const prev = (i - 1 + n) % n;
    const pt = lineLineIntersect(
      offsetEdges[prev].x1, offsetEdges[prev].y1, offsetEdges[prev].x2, offsetEdges[prev].y2,
      offsetEdges[i].x1, offsetEdges[i].y1, offsetEdges[i].x2, offsetEdges[i].y2
    );
    if (pt) result.push(pt);
    else result.push({ x: offsetEdges[i].x1, y: offsetEdges[i].y1 }); // fallback
  }

  // Validate: if resulting polygon collapsed or self-intersects, return empty
  if (result.length < 3 || polygonAreaXY(result) < 0.1) return [];
  return result;
}

// Line-line intersection helper
function lineLineIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) return null; // parallel
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  return { x: x1 + t * (x2 - x1), y: y1 + t * (y2 - y1) };
}

// Polygon area in m² (Shoelace formula on XY)
// ── Polygon overlap (used for obstacle blocking) ───────────────────────────
function segmentsIntersect(p1, p2, p3, p4) {
  const d = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x);
  if (Math.abs(d) < 1e-12) return false; // parallel / collinear — corner tests cover containment
  const t = ((p3.x - p1.x) * (p4.y - p3.y) - (p3.y - p1.y) * (p4.x - p3.x)) / d;
  const u = ((p3.x - p1.x) * (p2.y - p1.y) - (p3.y - p1.y) * (p2.x - p1.x)) / d;
  return t > 1e-9 && t < 1 - 1e-9 && u > 1e-9 && u < 1 - 1e-9;
}

/**
 * True if two simple polygons (XY, metres) overlap in area.
 * Checks corner containment both ways AND edge crossings, so a thin keep-out
 * strip (walkway) that passes *between* a panel's corners still blocks it,
 * and an obstacle that sits entirely inside a panel does too.
 */
export function polygonsOverlapXY(a, b) {
  if (!a?.length || !b?.length) return false;
  if (a.some((p) => pointInPolygonXY(p, b))) return true;
  if (b.some((p) => pointInPolygonXY(p, a))) return true;
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i], a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      if (segmentsIntersect(a1, a2, b[j], b[(j + 1) % b.length])) return true;
    }
  }
  return false;
}

export function polygonAreaXY(poly) {
  let area = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    area += poly[j].x * poly[i].y - poly[i].x * poly[j].y;
  }
  return Math.abs(area) / 2;
}

// ── Suggested inter-row spacing (based on tilt + latitude) ─────────────────
export function suggestedRowSpacing(panelLengthM, tiltDeg, lat) {
  // Minimum sun elevation at winter solstice ≈ (90 - lat - 23.5) degrees
  // Use 20° floor for safety in Indian context (lat 8°–37°)
  const minElev = Math.max(20, 90 - Math.abs(lat) - 23.5);
  const shadowLen = panelLengthM * Math.sin(tiltDeg * DEG);
  const spacing = shadowLen / Math.tan(minElev * DEG);
  return Math.max(0.3, Math.round(spacing * 100) / 100);
}


/** Stable identity of a panel across recalculations (used for "removed panels"). Face 0 keeps the legacy 4-part form. */
export function panelKey(p) {
  return p.face ? `${p.roofIdx}:f${p.face}:${p.row}:${p.col}:${p.tableSlot}` : `${p.roofIdx}:${p.row}:${p.col}:${p.tableSlot}`;
}

// ── Roof shape helpers (single slope / gable) ──────────────────────────────
/** Keep the part of polygon `poly` (XY) on the side of the line through p0 where dot(p - p0, n) >= 0. */
export function clipPolygonHalfPlane(poly, p0, n) {
  if (!poly?.length) return [];
  const side = (p) => (p.x - p0.x) * n.x + (p.y - p0.y) * n.y;
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const sa = side(a), sb = side(b);
    if (sa >= 0) out.push(a);
    if ((sa >= 0) !== (sb >= 0)) {
      const t = sa / (sa - sb);
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  return out.length >= 3 ? out : [];
}

/** Intersection of two convex XY polygons (Sutherland–Hodgman). */
export function clipConvexPolygons(subject, clip) {
  if (!subject?.length || !clip?.length) return [];
  // make clip CCW
  let area = 0;
  for (let i = 0; i < clip.length; i++) { const a = clip[i], b = clip[(i + 1) % clip.length]; area += a.x * b.y - b.x * a.y; }
  const c = area < 0 ? [...clip].reverse() : clip;
  let out = subject;
  for (let i = 0; i < c.length && out.length; i++) {
    const a = c[i], b = c[(i + 1) % c.length];
    const n = { x: -(b.y - a.y), y: b.x - a.x };   // inward normal for CCW
    out = clipPolygonHalfPlane(out, a, n);
  }
  return out;
}

/**
 * Roof surface height above the eave for a point, given the roof shape.
 *   flat  → 0
 *   mono  → rises away from the face azimuth (the roof slopes DOWN toward `azimuthDeg`)
 *   gable → ridge through the polygon's centre, perpendicular to the face direction; both halves slope down
 * Returns { z(pt), ridge: [pA, pB] | null, halves: [{poly, azimuthDeg}] }.
 */
export function roofSurface(roofXY, { roofShape = "flat", roofPitchDeg = 0, azimuthDeg = 180 } = {}) {
  const shape = roofPitchDeg > 0 ? roofShape : "flat";
  if (shape === "flat" || !roofXY?.length) return { z: () => 0, ridge: null, halves: [{ poly: roofXY, azimuthDeg }], shape: "flat" };
  const az = azimuthDeg * DEG;
  const face = { x: Math.sin(az), y: Math.cos(az) };            // downhill direction (the face the roof looks at)
  const t = roofXY.map((p) => p.x * face.x + p.y * face.y);
  const tMin = Math.min(...t), tMax = Math.max(...t);
  const slope = Math.tan(roofPitchDeg * DEG);
  if (shape === "mono") {
    // highest edge is the one furthest from the face direction (t = tMin)
    return { z: (p) => (tMax - (p.x * face.x + p.y * face.y)) * slope, ridge: null, halves: [{ poly: roofXY, azimuthDeg }], shape };
  }
  // gable: ridge at the middle of the projection range
  const tMid = (tMin + tMax) / 2;
  const half = (tMax - tMin) / 2;
  const along = { x: face.y, y: -face.x };                       // ridge direction
  const c = { x: 0, y: 0 };
  roofXY.forEach((p) => { c.x += p.x / roofXY.length; c.y += p.y / roofXY.length; });
  // a point on the ridge line: move the centroid so its projection on `face` is tMid
  const cT = c.x * face.x + c.y * face.y;
  const r0 = { x: c.x + (tMid - cT) * face.x, y: c.y + (tMid - cT) * face.y };
  const ext = Math.max(...roofXY.map((p) => Math.abs((p.x - r0.x) * along.x + (p.y - r0.y) * along.y))) + 1;
  const ridge = [{ x: r0.x - along.x * ext, y: r0.y - along.y * ext }, { x: r0.x + along.x * ext, y: r0.y + along.y * ext }];
  const frontHalf = clipPolygonHalfPlane(roofXY, r0, face);      // t >= tMid → slopes down toward azimuth
  const backHalf = clipPolygonHalfPlane(roofXY, r0, { x: -face.x, y: -face.y });
  const halves = [];
  if (frontHalf.length) halves.push({ poly: frontHalf, azimuthDeg });
  if (backHalf.length) halves.push({ poly: backHalf, azimuthDeg: (azimuthDeg + 180) % 360 });
  return { z: (p) => (half - Math.abs((p.x * face.x + p.y * face.y) - tMid)) * slope, ridge, halves, shape };
}

// ── Sun position → horizontal shadow offset per metre of height (XY metres) ─
/** Winter-solstice sun at a given hour angle (0 = solar noon, −45 ≈ 9 am, +45 ≈ 3 pm). null when the sun is too low. */
export function sunOffsetAt(latitudeDeg, hourAngleDeg) {
  const latRad = latitudeDeg * DEG, decl = -23.45 * DEG, haRad = hourAngleDeg * DEG;
  const sinAlt = Math.sin(latRad) * Math.sin(decl) + Math.cos(latRad) * Math.cos(decl) * Math.cos(haRad);
  if (sinAlt <= 0.05) return null;
  const sunAlt = Math.asin(sinAlt);
  const cosAz = (sinAlt * Math.sin(latRad) - Math.sin(decl)) / (Math.cos(sunAlt) * Math.cos(latRad));
  const sinAz = Math.sin(haRad) * Math.cos(decl) / Math.cos(sunAlt);
  const sunAzimuth = Math.atan2(sinAz, cosAz);   // from south, shadow falls this way (north-ish)
  const invTan = 1 / Math.tan(sunAlt);
  if (invTan > 50) return null;
  return { dx: Math.sin(sunAzimuth) * invTan, dy: Math.cos(sunAzimuth) * invTan, altitudeDeg: sunAlt / DEG, azimuthDeg: (180 + sunAzimuth / DEG + 360) % 360 };
}

/**
 * Shadows cast by panel rows onto the roof plane and onto the panels behind them,
 * for one sun position. `panels3d` = [{ key, footprint: [{x,y}×4], zFront, zBack, roofIdx }]
 * where corners 0/3 are the low (sun-facing) edge and 1/2 the high edge (heights above the roof plane).
 * Returns { ground: [{key, poly}], onPanels: [{key, poly, fraction}], shadedKeys: Set }.
 */
export function computeRowShading(panels3d, sun) {
  if (!sun || !panels3d?.length) return { ground: [], onPanels: [], shadedKeys: new Set() };
  const off = (p, h) => ({ x: p.x + sun.dx * h, y: p.y + sun.dy * h });
  // spatial hash for neighbour lookup
  const cell = 4;
  const grid = new Map();
  const keyOf = (x, y) => `${Math.floor(x / cell)}:${Math.floor(y / cell)}`;
  panels3d.forEach((p, i) => {
    const xs = p.footprint.map((c) => c.x), ys = p.footprint.map((c) => c.y);
    for (let gx = Math.floor(Math.min(...xs) / cell); gx <= Math.floor(Math.max(...xs) / cell); gx++)
      for (let gy = Math.floor(Math.min(...ys) / cell); gy <= Math.floor(Math.max(...ys) / cell); gy++) {
        const k = `${gx}:${gy}`; if (!grid.has(k)) grid.set(k, []); grid.get(k).push(i);
      }
  });
  const ground = [], onPanels = [], shadedKeys = new Set();
  const areaOf = (poly) => polygonAreaXY(poly);
  panels3d.forEach((p, i) => {
    const f = p.footprint;
    // shadow of the tilted slab: hull of footprint ∪ front edge shifted by zFront ∪ back edge shifted by zBack
    const pts = [...f, off(f[0], p.zFront), off(f[3], p.zFront), off(f[1], p.zBack), off(f[2], p.zBack)];
    const hull = convexHullXY(pts);
    if (hull.length < 3) return;
    ground.push({ key: p.key, poly: hull });
    // neighbours whose footprint overlaps the shadow
    const xs = hull.map((c) => c.x), ys = hull.map((c) => c.y);
    const seen = new Set();
    for (let gx = Math.floor(Math.min(...xs) / cell); gx <= Math.floor(Math.max(...xs) / cell); gx++)
      for (let gy = Math.floor(Math.min(...ys) / cell); gy <= Math.floor(Math.max(...ys) / cell); gy++)
        (grid.get(`${gx}:${gy}`) || []).forEach((j) => {
          if (j === i || seen.has(j) || panels3d[j].roofIdx !== p.roofIdx) return;
          seen.add(j);
          const q = panels3d[j];
          // only panels "behind" (in the shadow direction) can be shaded by this one
          const cx = q.footprint.reduce((a, c) => a + c.x, 0) / 4 - f.reduce((a, c) => a + c.x, 0) / 4;
          const cy = q.footprint.reduce((a, c) => a + c.y, 0) / 4 - f.reduce((a, c) => a + c.y, 0) / 4;
          if (cx * sun.dx + cy * sun.dy <= 0) return;
          const inter = clipConvexPolygons(q.footprint, hull);
          if (inter.length < 3) return;
          const fraction = areaOf(inter) / Math.max(areaOf(q.footprint), 1e-6);
          if (fraction < 0.02) return;
          onPanels.push({ key: q.key, byKey: p.key, poly: inter, fraction });
          shadedKeys.add(q.key);
        });
  });
  return { ground, onPanels, shadedKeys };
}

function convexHullXY(points) {
  const pts = points.map((p) => ({ lat: p.y, lng: p.x }));
  return convexHull(pts).map((p) => ({ x: p.lng, y: p.lat }));
}

// ── Main panel placement algorithm ─────────────────────────────────────────
/**
 * Returns { panels: [{center:{lat,lng}, corners:[{lat,lng}×4], row, col}],
 *           totalPanels, totalKwp, totalAreaM2 }
 */
export function calculateLayout(params) {
  const {
    roofPolygons,       // [[{lat, lng}]]
    obstaclePolygons,   // [[{lat, lng}]]
    shadowPolygons,     // [[{lat, lng}]] — computed shadow zones to exclude
    panelLengthM,       // e.g. 2.1
    panelWidthM,        // e.g. 1.0
    panelWattageW,      // e.g. 540
    azimuthDeg,         // 180 = south
    tiltDeg,            // 15
    interRowSpacingM,   // gap between tables (walkway)
    colGapM = 0.02,     // gap between panels within a table (depth) and along row
    setbackM = 0.3,     // edge setback
    orientation = "portrait",  // "portrait" or "landscape"
    modulesPerGroup = 1,       // modules stacked in tilt/face direction per table (1P, 2P, 3P...)
    mountType = "flat",        // "flat" = tilted racks (walkway between rows); "pitched" = flush on slope
    roofShape = "flat",        // "flat" | "mono" | "gable" (pitched roofs)
    roofPitchDeg = 0,          // slope of the roof surface (pitched roofs)
  } = params;

  if (!roofPolygons?.length) return { panels: [], totalPanels: 0, totalKwp: 0, totalAreaM2: 0 };

  // Combine obstacles + shadow zones for exclusion
  const allObstacles = [...(obstaclePolygons || []), ...(shadowPolygons || [])];

  // Derive centroid origin for XY conversion
  const allPts = roofPolygons.flat();
  const origin = polygonCentroid(allPts);

  // Orientation: which dimension goes along face (tilt) vs along row
  const faceDim = orientation === "portrait" ? panelLengthM : panelWidthM;
  const rowDim  = orientation === "portrait" ? panelWidthM  : panelLengthM;

  // Flush on a sloped roof: the module tilt IS the roof pitch
  const effTilt = mountType === "pitched" && roofPitchDeg > 0 ? roofPitchDeg : tiltDeg;
  // Horizontal footprint of a single tilted panel (along face direction)
  const singleHorizDepth = faceDim * Math.cos(effTilt * DEG);

  // Table = N modules stacked in the face direction
  const mpg = Math.max(1, Math.round(modulesPerGroup));
  const tableDepth = mpg * singleHorizDepth + (mpg - 1) * colGapM;
  // Row pitch = table depth + the gap the user asked for (flush roofs usually use a
  // small maintenance gap; the field is honoured in both modes).
  const effInterRow = Math.max(0.02, interRowSpacingM || 0);
  const rowPitch = tableDepth + effInterRow;
  // Column pitch (along row direction)
  const colPitch = rowDim + colGapM;

  const allPanels = [];
  let totalArea = 0;
  const MAX_PANELS = 3000; // safety cap — prevents OOM when a huge roof is drawn at low zoom
  let capped = false;

  for (const roof of roofPolygons) {
    if (capped) break;
    const roofXY = roof.map((p) => latlngToXY(p.lat, p.lng, origin.lat, origin.lng));
    // Convert obstacles to XY
    const obsXY = (allObstacles || []).map((obs) =>
      obs.map((p) => latlngToXY(p.lat, p.lng, origin.lat, origin.lng))
    );
    // A two-sided (gable) roof is two faces with opposite azimuths; each face is laid out on its own
    const faces = mountType === "pitched" && roofShape === "gable" && roofPitchDeg > 0
      ? roofSurface(roofXY, { roofShape, roofPitchDeg, azimuthDeg }).halves
      : [{ poly: roofXY, azimuthDeg }];
    let faceNo = 0;
    for (const facePiece of faces) {
    if (capped) break;
    const faceIdx = faceNo++;
    const az = facePiece.azimuthDeg * DEG;
    const faceDir = { x: Math.sin(az), y: Math.cos(az) };
    // Row direction = 90° CW from face  →  (faceDir.y, -faceDir.x)
    const rowDir = { x: faceDir.y, y: -faceDir.x };
    const shrunk = shrinkPolygon(facePiece.poly, setbackM);
    if (!shrunk.length) continue;
    totalArea += polygonAreaXY(shrunk);

    // Project shrunk polygon onto (face, row) axes to get bounding box
    const tCoords = shrunk.map((p) => p.x * faceDir.x + p.y * faceDir.y);
    const sCoords = shrunk.map((p) => p.x * rowDir.x + p.y * rowDir.y);
    const tMin = Math.min(...tCoords), tMax = Math.max(...tCoords);
    const sMin = Math.min(...sCoords), sMax = Math.max(...sCoords);

    let rowIdx = 0;
    // Small epsilon prevents centers from landing exactly on polygon boundary
    const EPS = 0.001;
    for (let tStart = tMin + EPS; tStart + tableDepth <= tMax; tStart += rowPitch) {
      if (allPanels.length >= MAX_PANELS) { capped = true; break; }
      let colIdx = 0;
      let s = sMin + rowDim / 2 + EPS;

      while (s + rowDim / 2 <= sMax) {
        if (allPanels.length >= MAX_PANELS) { capped = true; break; }
        let allPlacedInColumn = true;
        const columnPanels = [];

        // Try placing all N modules stacked in face direction at this column position
        for (let g = 0; g < mpg; g++) {
          const panelT = tStart + singleHorizDepth / 2 + g * (singleHorizDepth + colGapM);
          const cx = panelT * faceDir.x + s * rowDir.x;
          const cy = panelT * faceDir.y + s * rowDir.y;

          const hd = singleHorizDepth / 2, pw = rowDim / 2;
          const corners = [
            { x: cx + hd * faceDir.x + pw * rowDir.x, y: cy + hd * faceDir.y + pw * rowDir.y },
            { x: cx - hd * faceDir.x + pw * rowDir.x, y: cy - hd * faceDir.y + pw * rowDir.y },
            { x: cx - hd * faceDir.x - pw * rowDir.x, y: cy - hd * faceDir.y - pw * rowDir.y },
            { x: cx + hd * faceDir.x - pw * rowDir.x, y: cy + hd * faceDir.y - pw * rowDir.y },
          ];

          // Check: panel center must be inside the shrunk roof polygon
          const centerInRoof = pointInPolygonXY({ x: cx, y: cy }, shrunk);
          if (!centerInRoof) { allPlacedInColumn = false; break; }

          // Check obstacles/keep-outs/shadows: any area overlap blocks the panel
          // (corner-in-obstacle, obstacle-in-panel, or crossing edges — see polygonsOverlapXY)
          const blocked = obsXY.some((obs) => polygonsOverlapXY(corners, obs));
          if (blocked) { allPlacedInColumn = false; break; }

          columnPanels.push({
            center: xyToLatlng(cx, cy, origin.lat, origin.lng),
            corners: corners.map((c) => xyToLatlng(c.x, c.y, origin.lat, origin.lng)),
            row: rowIdx,
            col: colIdx,
            tableSlot: g,
            face: faceIdx,
            azimuthDeg: facePiece.azimuthDeg,
            tiltDeg: effTilt,
          });
        }

        // Only add the full table column if ALL modules fit
        if (allPlacedInColumn && columnPanels.length === mpg) {
          allPanels.push(...columnPanels);
        }

        colIdx++;
        s += colPitch;
      }
      rowIdx++;
    }
    }   // faces
  }

  return {
    panels: allPanels,
    totalPanels: allPanels.length,
    totalKwp: Number(((allPanels.length * panelWattageW) / 1000).toFixed(2)),
    totalAreaM2: Math.round(totalArea),
    capped,
  };
}
